import React, { FC, useEffect, useContext } from 'react';
import _, { min } from 'lodash';
import { Skeleton } from 'antd';
import {
    calculateFontSize,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getConfig,
    getMaxBound,
    getMinBound,
    removeFunctionFromProps,
} from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
const G2 = require('@antv/g2');

const defaultLabelWordLimit = 5;

//antv g2 doesnt render these string in tooltip and returns empty string,
//inorder to avoid them from displaying empty string, displaying alternative name
const replacementKeyMapping: any = {
    '<NA>': 'NA',
    '<na>': 'na',
};

const offsetXDistance = -1 * calculateFontSize(window.innerWidth, 5);

//ADD ALL THE REQUIRED PROPS AT TOP
interface Props {
    fields: Array<string>;
    dataset: Array<any>;
    chartContainer: string;
    height: string | number;
    loading: boolean;
    showLoader?: boolean;
    yAxisConfig?: boolean | object;
    xAxisConfig?: boolean | object;
    chartConfig?: object;
    yAxisLabelFormatter?: (val: any) => any;
    xAxisLabelFormatter?: (val: any) => any;
    tooltipConfig?: object;
    YGrid?: boolean;
    XGrid?: boolean;
    padding?: [number, number, number, number];
    width?: number;
    barWidth?: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    error?: any;
    additionalStyle?: any;
    minBound?: number;
    maxBound?: number;
    minBarChart?: number;
    maxBarChart?: number;
    showLegend?: boolean;
    showErrorBox?: boolean;
    customTooltip?: any[];
    transpose?: boolean;
    Yline?: boolean;
    Xline?: boolean;

    enableGroupedChart?: boolean;
    enableGroupedPointChart?: boolean;
    intervalLabelTooltipKey?: string;
    groupedChartColor?: [string, string];
    groupedPointColor?: [string, string];
    groupedChartTooltip?: string;
    groupedPointChartTooltip?: string;
    intervalStyle?: object;
    disableIntervalLabel?: boolean;
    tooltipFormatter?: (xAxis: any, val: any) => any;
    pointChartTooltipFormatter?: (xAxis: any, val: any) => any;
    addPercentageInIntervalLabel?: boolean;
    includeBarChart?: boolean;
    includePointsChart?: boolean;
    includeLineChart?: boolean;
    pointColors?: string[];
    shapeTypes?: string[];
    pointSize: number;
    barColor: string;
    lineChartColor?: string;
    onItemClick?: Function;
}

const PointChart: FC<Props> = ({
    yAxisConfig = true,
    xAxisConfig = true,
    chartConfig = {},
    height = 400,
    width,
    fields,
    xAxisTitle = '',
    yAxisTitle = '',
    additionalStyle,
    dataset,
    chartContainer,
    tooltipConfig = {},
    loading,
    padding = [0, 0, 0, 0],
    error,
    barWidth = 20,
    YGrid = true,
    XGrid = false,
    Yline = false,
    Xline = true,
    minBarChart,
    maxBarChart,
    showLoader = true,
    showErrorBox = true,
    minBound,
    showLegend = false,
    yAxisLabelFormatter,
    xAxisLabelFormatter,
    intervalLabelTooltipKey = fields[1],
    tooltipFormatter = (xAxis: any, val: any) => {
        return {
            name: xAxis,
            value: addPercentageInIntervalLabel ? formatNumWithKMB(val) : formatNumWithKMBAlt(val, 2, 2),
        };
    },
    pointChartTooltipFormatter = (xAxis: any, val: any) => {
        return {
            name: xAxis,
            value: addPercentageInIntervalLabel ? formatNumWithKMB(val) : formatNumWithKMBAlt(val, 2, 2),
        };
    },
    maxBound,
    transpose,
    intervalStyle = {},
    disableIntervalLabel = false,
    addPercentageInIntervalLabel = false,
    includeBarChart = false,
    includeLineChart = false,
    includePointsChart = true,
    pointColors,
    shapeTypes,
    pointSize,
    barColor,
    customTooltip,
    lineChartColor,
    onItemClick,
}) => {
    // if (!loading && error && dataset?.length === 0 && showErrorBox) {
    //     return <ErrorBox msg={String(error)} />;
    // }
    const isWindowTabActive = useTrackTabChange();

    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;
    useEffect(() => {
        if (!window) return;
        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
            position: 'left',
        };

        const min = !(_.isNull(minBound) || _.isUndefined(minBound)) ? minBound : getMinBound(dataset, fields);
        const max = !(_.isNull(maxBound) || _.isUndefined(maxBound)) ? maxBound : getMaxBound(dataset, fields);
        const chart = new G2.Chart({
            container: chartContainer,
            height: height,
            width: width,
            autoFit: width ? false : true,
            appendPadding: padding,
        });

        chart.data(dataset);

        // chart.scale(fields[0], {
        //     range: [0, 1],
        // });
        if (includePointsChart) {
            //for point chart
            chart.scale(fields[1], {
                nice: true,
                min: min,
                max: max,
                ...chartConfig,
            });
        }

        if (includeLineChart) {
            chart.scale('mraup', {
                // nice: true,
                min: min,
                max: max,
                ...chartConfig,
            });
        }

        // for bar chart
        if (includeBarChart) {
            chart.scale(fields[2], {
                nice: true,
                min: minBarChart,
                max: maxBarChart,
                ...chartConfig,
            });
        }
        if (yAxisConfig) {
            chart.axis(fields[1], {
                style: {
                    textAlign: 'center',
                },
                label: !yAxisLabelFormatter
                    ? null
                    : {
                          formatter: yAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                line: Yline ? {} : null,
                grid: YGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !yAxisTitle
                    ? null
                    : {
                          text: yAxisTitle,
                          position: 'middle',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                position: 'left',
            });
        }
        if (!yAxisConfig) chart.axis(fields[1], false);
        if (!xAxisConfig) chart.axis(fields[0], false);
        if (xAxisConfig) {
            chart.axis(fields[0], {
                label: !xAxisLabelFormatter
                    ? null
                    : {
                          formatter: xAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                          //   offset: 0,
                      },
                tickLine: {
                    alignTick: true,
                },
                line: Xline ? {} : null,
                grid: XGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !xAxisTitle
                    ? null
                    : {
                          text: xAxisTitle,
                          position: 'start',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
            });
        }
        if (includeBarChart) {
            chart.axis(fields[2], false);
            chart
                .interval()
                .position(`${fields[0]}*${fields[2]}`)
                .color(`${barColor}`)
                .size(barWidth)
                .label(`${intervalLabelTooltipKey}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    const formattedValue = addPercentageInIntervalLabel
                        ? formatNumWithKMB(val)
                        : formatNumWithKMBAlt(val, 2, 2);
                    return {
                        position: pos,
                        offsetX: transpose ? (val < 0 ? offsetXDistance * formattedValue.length : -8) : 0,
                        offsetY: transpose ? 0 : val < 0 ? -10 : 10,
                        content: addPercentageInIntervalLabel ? formattedValue + '%' : formattedValue,
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(`${fields[0]}*${intervalLabelTooltipKey}`, (xAxis: any, val: any) => {
                    let temp = xAxis;
                    if (temp in replacementKeyMapping) temp = replacementKeyMapping[temp];
                    return tooltipFormatter(temp, val);
                })
                .style({ ...intervalStyle });
        }
        if (includePointsChart) {
            //point chart
            chart
                .point()
                .position(`${fields[0]}*${fields[1]}`)
                .color(fields[3], pointColors)
                .shape(fields[3], shapeTypes)
                .size(pointSize);
            //.tooltip(false);
        }

        if (includeLineChart) {
            //line chart
            chart.axis(fields[4], false);
            chart.line().position(`${fields[0]}*${fields[4]}`).color(lineChartColor).tooltip(true);
        }

        //just for tooltip

        chart.tooltip({
            ...defaultTooltipConfig,
            ...tooltipConfig,
            containerTpl:
                '<div class="g2-tooltip">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
            customItems: (items: any[], index: number) => {
                if (customTooltip) {
                    const filteredItem = customTooltip?.filter((item) => {
                        return item?.[0]?.title === items?.[0]?.title;
                    });
                    return [...filteredItem?.[0]];
                }
                return items;
            },
        });
        if (onItemClick) {
            let currentElement = '';
            chart.on('tooltip:show', (...args: any) => {
                currentElement = args?.[0]?.data?.title;
                // console.log(args?.[0]?.data?.title, 'logging tool');
            });
            chart.on('plot:click', (...args: any) => {
                onItemClick(currentElement);
            });
        }

        if (!showLegend) chart.legend(false);
        else
            chart.legend({
                position: 'top',
                layout: 'horizontal',
            });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, barColor, isWindowTabActive]);

    return (
        <div className="">
            {showLoader && (
                <>
                    <Skeleton loading={loading} />
                    <Skeleton loading={loading} />
                </>
            )}
            <div style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }} id={chartContainer} />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps }); //remove function type from props for referential equality
    return _.isEqual(clonePrev, cloneNext);
}

export default React.memo(PointChart, arePropsEqual);
